import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent, type KeyboardEvent as RKeyboardEvent } from 'react';

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(query).matches));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = () => setMatch(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [query]);
  return match;
}

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';
export function useBreakpoint(): Breakpoint {
  const desktop = useMediaQuery('(min-width: 1280px)');
  const tablet = useMediaQuery('(min-width: 768px)');
  return desktop ? 'desktop' : tablet ? 'tablet' : 'mobile';
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** localStorage-backed state; every access wrapped in try/catch. */
export function useLocalStorage<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T | ((p: T) => T)) => {
      setValue((prev) => {
        const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [key],
  );
  return [value, set];
}

/** Global keyboard shortcuts, ignored while typing in inputs. */
export function useHotkeys(map: Record<string, (e: KeyboardEvent) => void>, enabled = true) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (typing && e.key !== 'Escape') return;
      const fn = ref.current[e.key];
      if (fn) fn(e);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled]);
}

/** Draggable divider width persisted in localStorage. */
export function useResizable(key: string, initial: number, min: number, max: number, direction: 1 | -1 = 1) {
  const [width, setWidth] = useLocalStorage<number>(`mp:width:${key}`, initial);
  const [dragging, setDragging] = useState(false);
  const clamp = useCallback((w: number) => Math.max(min, Math.min(max, w)), [min, max]);
  const onPointerDown = useCallback(
    (e: RPointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      setDragging(true);
      const move = (ev: PointerEvent) => setWidth(clamp(startW + direction * (ev.clientX - startX)));
      const up = () => {
        setDragging(false);
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [width, setWidth, clamp, direction],
  );
  const onKeyDown = useCallback(
    (e: RKeyboardEvent) => {
      if (e.key === 'ArrowLeft') setWidth(clamp(width - 16 * direction));
      if (e.key === 'ArrowRight') setWidth(clamp(width + 16 * direction));
    },
    [width, setWidth, clamp, direction],
  );
  return { width: clamp(width), dragging, handleProps: { onPointerDown, onKeyDown } };
}
